// @flow
import { capitalize } from '../../../../helpers/capitalize';

import type { Record } from '../../../../flow.types';
import { Params } from 'src/app/App';

export type COLOR = {
  id: string;
  name: string;
};

type PreFacetsExtractor = Record<
  'color' | string,
  (arg: COLOR | unknown) => string
>;

/***
 * @summary Get Pre Facet Engagement information.
 * @description This Function extracts pre facet engagement information
 * from params using rules setted by **extractors**.
 *
 *
 * The **extractor** is an object with every property being mapped at params
 * and a function that extracts information from its mapped param.
 *
 *
 * @param {String} categoryName - The First part of engagement variable
 * @param {Object} params - The params object with information to be extracted
 * @param {PreFacetsExtractor} extractors - The set of rules that will be used
 * to extract information from params, every property is mapped to a params
 * object key and is a function that will be called to extract the information
 * from that property
 *
 * @return {string|null}
 * @see {@link https://gapinc.atlassian.net/browse/SEO-1706|SEO-1706}
 *
 * @example
 * getPreFacetEngagement("Category", {
 *    color: { name: "blue" }
 *  }, {
 *    color: color => color.name
 *  });
 *  // returns "Category:Color:Blue"
 */
export const getPreFacetEngagement = (
  categoryName: string,
  params: Params,
  extractors: PreFacetsExtractor = {
    color: (color: COLOR) => capitalize(color.name),
  }
): string | null => {
  const facets = !!params
    ? Object.entries(params)
        .filter(([key]) => Object.keys(extractors).includes(key))
        .map(([key, value]) => [capitalize(key), extractors[key](value)])
    : [];

  if (!facets.length) {
    return null;
  }

  return [categoryName]
    .concat(...facets)
    .map<string>((engagement) => engagement.trim())
    .join(':');
};
